/**
 * v4-cart-drawer.js
 * Progressive enhancement: intercepts product form submits, fetches cart via AJAX,
 * updates the drawer and header counter, opens the drawer.
 * Falls back to native form submit if JS fails at any point.
 * No external dependencies.
 */

(function () {
  'use strict';

  /* ---- DOM refs ---- */
  var drawer = document.getElementById('v4-cart-drawer');
  var overlay = document.getElementById('v4-cart-overlay');
  var loading = document.getElementById('v4-cd-loading');
  var body = document.getElementById('v4-cd-body');
  var foot = document.getElementById('v4-cd-foot');
  var countEl = document.getElementById('v4-cd-count');
  var subtotalEl = document.getElementById('v4-cd-subtotal');
  var liveRegion = document.getElementById('v4-cart-live-region');
  var shipWrap = document.getElementById('v4-cd-ship');
  var shipMsg = document.getElementById('v4-cd-ship-msg');
  var shipTrack = document.getElementById('v4-cd-ship-track');
  var shipFill = document.getElementById('v4-cd-ship-fill');
  var upsellWrap = document.getElementById('v4-cd-upsell');
  var upsellRow = document.getElementById('v4-cd-upsell-row');

  // Bail silently — drawer not present in this page context
  if (!drawer) return;

  /* ---- Focus trap helpers ---- */
  var FOCUSABLE = 'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])';

  function getFocusable() {
    return Array.prototype.slice.call(drawer.querySelectorAll(FOCUSABLE)).filter(function (el) {
      return el.offsetParent !== null;
    });
  }

  /* ---- Open / close ---- */
  var _previousFocus = null;

  function openDrawer() {
    drawer.removeAttribute('inert');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) {
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
    }
    document.body.style.overflow = 'hidden';
    _previousFocus = document.activeElement;
    maybeLoadUpsell();
    // Focus the close button on open
    var closeBtn = drawer.querySelector('[data-cart-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    if (overlay) {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
    }
    document.body.style.overflow = '';
    if (_previousFocus) {
      _previousFocus.focus();
      _previousFocus = null;
    }
  }

  /* ---- Focus trap (Tab cycling) ---- */
  drawer.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var focusable = getFocusable();
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  /* ---- Close triggers ---- */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
      closeDrawer();
    }
  });

  document.querySelectorAll('[data-cart-close]').forEach(function (el) {
    el.addEventListener('click', closeDrawer);
  });

  /* ---- Header cart button: open drawer instead of navigating (JS path) ---- */
  var cartBtn = document.querySelector('[data-cart-link]');
  if (cartBtn) {
    cartBtn.addEventListener('click', function (e) {
      e.preventDefault();
      openDrawer();
    });
  }

  /* ---- Loading state helpers ---- */
  function showLoading() {
    if (loading) {
      loading.classList.add('is-visible');
      loading.setAttribute('aria-hidden', 'false');
    }
  }

  function hideLoading() {
    if (loading) {
      loading.classList.remove('is-visible');
      loading.setAttribute('aria-hidden', 'true');
    }
  }

  /* ---- Money formatter ---- */
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    // fr-CA style: space as thousands separator, comma as decimal
    return (cents / 100).toLocaleString('fr-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2
    });
  }

  /* ---- Update header cart count badge ---- */
  function updateHeaderCount(count) {
    // All elements bearing data-cart-count
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
    // Legacy v4-header__cart-count span (re-render via DOM)
    var badge = document.querySelector('.v4-header__cart-count');
    if (badge) {
      badge.textContent = count;
      badge.style.display = count > 0 ? '' : 'none';
    } else if (count > 0) {
      // Create badge if it didn't exist (e.g., first item added)
      var cartLink = document.querySelector('[data-cart-link]');
      if (cartLink) {
        var newBadge = document.createElement('span');
        newBadge.className = 'v4-header__cart-count';
        newBadge.textContent = count;
        cartLink.appendChild(newBadge);
      }
    }
  }

  /* ---- Free shipping progress bar ---- */
  function updateShippingBar(totalCents) {
    if (!shipWrap || !shipMsg || !shipFill) return;
    var threshold = parseInt(shipWrap.getAttribute('data-threshold'), 10) || 10000;
    var pct = Math.min(100, Math.round((totalCents / threshold) * 100));
    var remaining = threshold - totalCents;

    shipFill.style.width = pct + '%';
    if (shipTrack) shipTrack.setAttribute('aria-valuenow', pct);

    var i18n = window.V4CartI18n || {};
    if (remaining > 0) {
      shipWrap.classList.remove('is-free');
      var tpl = i18n.shipRemaining || 'Plus que @AMOUNT@ pour la livraison gratuite 🎉';
      shipMsg.textContent = tpl.replace('@AMOUNT@', formatMoney(remaining));
    } else {
      shipWrap.classList.add('is-free');
      shipMsg.textContent = i18n.shipFree || 'Vous avez la livraison gratuite !';
    }
  }

  /* ---- Render cart items into the drawer body ---- */
  function renderCart(cart) {
    if (!body) return;

    if (cart.item_count === 0) {
      var i18nEmpty = window.V4CartI18n || {};
      body.innerHTML =
        '<div class="v4-cd__empty" id="v4-cd-empty">' +
          '<div class="v4-cd__empty-icon">' +
            '<svg viewBox="0 0 24 24"><path d="M5 7h14l-1.2 11.2A2 2 0 0 1 15.8 20H8.2a2 2 0 0 1-2-1.8L5 7Z"/><path d="M9 7V5a3 3 0 0 1 6 0v2"/></svg>' +
          '</div>' +
          '<p>' + escapeHtml(i18nEmpty.empty || 'Votre panier est vide.') + '</p>' +
          '<a href="' + escapeHtml(i18nEmpty.discoverUrl || '/collections') + '" class="v4-cd__empty-cta" data-cart-close>' + escapeHtml(i18nEmpty.discover || 'Découvrir la boutique') + '</a>' +
        '</div>';
      if (foot) foot.style.display = 'none';
      // Re-attach close on empty CTA
      var emptyCta = body.querySelector('[data-cart-close]');
      if (emptyCta) emptyCta.addEventListener('click', closeDrawer);
    } else {
      var itemsHtml = '<ul class="v4-cd__items" id="v4-cd-items">';
      cart.items.forEach(function (item) {
        var imgHtml = item.image
          ? '<img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.title) + '" width="80" height="80" loading="lazy" class="v4-cd__item-img">'
          : '<div class="v4-cd__item-img" aria-hidden="true"></div>';
        var variantHtml = item.variant_title && item.variant_title !== 'Default Title'
          ? '<p class="v4-cd__item-variant">' + escapeHtml(item.variant_title) + '</p>'
          : '';
        itemsHtml +=
          '<li class="v4-cd__item">' +
            imgHtml +
            '<div class="v4-cd__item-info">' +
              '<p class="v4-cd__item-title">' + escapeHtml(item.product_title) + '</p>' +
              variantHtml +
              '<p class="v4-cd__item-qty">' + escapeHtml((window.V4CartI18n && window.V4CartI18n.qty) || 'Qté :') + ' ' + item.quantity + '</p>' +
            '</div>' +
            '<span class="v4-cd__item-price">' + formatMoney(item.final_line_price) + '</span>' +
          '</li>';
      });
      itemsHtml += '</ul>';
      body.innerHTML = itemsHtml;

      if (foot) foot.style.display = '';
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    }

    // Update count label in drawer header
    if (countEl) countEl.textContent = '(' + cart.item_count + ')';

    updateShippingBar(cart.total_price);
    updateUpsell(cart);
  }

  /* ---- AOV upsell: complementary first, related fallback, max 2 cards ---- */
  var _upsellLoaded = false;

  function maybeLoadUpsell() {
    if (_upsellLoaded || !upsellWrap) return;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { updateUpsell(cart); })
      .catch(function () {});
  }

  function updateUpsell(cart) {
    if (!upsellWrap || !upsellRow) return;
    _upsellLoaded = true;
    if (!cart || cart.item_count === 0) { upsellWrap.hidden = true; upsellRow.innerHTML = ''; return; }
    var inCart = cart.items.map(function (i) { return i.product_id; });
    var seed = cart.items[0].product_id;

    function renderProducts(products) {
      var picks = products.filter(function (p) {
        return p.available !== false && inCart.indexOf(p.id) === -1;
      }).slice(0, 2);
      upsellRow.innerHTML = '';
      if (!picks.length) { upsellWrap.hidden = true; return; }
      picks.forEach(function (p) {
        var variant = null;
        if (p.variants && p.variants.length) {
          for (var i = 0; i < p.variants.length; i++) {
            if (p.variants[i].available) { variant = p.variants[i]; break; }
          }
          if (!variant) variant = p.variants[0];
        }
        if (!variant) return;
        var img = p.featured_image || (p.images && p.images[0]) || '';
        if (img) img += (img.indexOf('?') > -1 ? '&' : '?') + 'width=88';
        var card = document.createElement('div');
        card.className = 'v4-cd__up';
        var imHtml = img ? '<img src="' + escapeHtml(img) + '" alt="" width="44" height="44" loading="lazy">' : '';
        card.innerHTML =
          imHtml +
          '<div class="v4-cd__up-info">' +
            '<p class="v4-cd__up-title">' + escapeHtml(p.title) + '</p>' +
            '<span class="v4-cd__up-price">' + formatMoney(variant.price) + '</span>' +
          '</div>' +
          '<button type="button" data-quick-add data-variant-id="' + variant.id + '" aria-label="' + escapeHtml(p.title) + '">+</button>';
        upsellRow.appendChild(card);
      });
      upsellWrap.hidden = upsellRow.children.length === 0;
    }

    function fetchRecs(intent, done) {
      fetch('/recommendations/products.json?product_id=' + seed + '&limit=6&intent=' + intent, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (d) { done((d && d.products) || []); })
        .catch(function () { done([]); });
    }

    fetchRecs('complementary', function (products) {
      if (products.length) { renderProducts(products); }
      else { fetchRecs('related', renderProducts); }
    });
  }

  /* External cart changes (e.g. quick-add from collection cards) re-render the drawer */
  document.addEventListener('v4cart:refresh', function (e) {
    if (e.detail && typeof e.detail.item_count !== 'undefined') {
      renderCart(e.detail);
      updateHeaderCount(e.detail.item_count);
    }
  });

  /* ---- HTML escape helper ---- */
  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* ---- Announce to screen reader ---- */
  function announce(msg) {
    if (!liveRegion) return;
    liveRegion.textContent = '';
    // Small delay to force re-announcement
    setTimeout(function () {
      liveRegion.textContent = msg;
    }, 50);
  }

  /* ---- Core: add to cart via fetch ---- */
  function addToCart(form) {
    var formData = new FormData(form);
    showLoading();

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
      body: formData
    })
      .then(function (res) {
        if (!res.ok) throw new Error('add_failed');
        return res.json();
      })
      .then(function (addedItem) {
        // Fetch full cart state
        return fetch('/cart.js', {
          headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' }
        }).then(function (res) {
          if (!res.ok) throw new Error('cart_fetch_failed');
          return res.json();
        }).then(function (cart) {
          renderCart(cart);
          updateHeaderCount(cart.item_count);
          hideLoading();
          openDrawer();
          var addedTpl = (window.V4CartI18n && window.V4CartI18n.added) || 'Produit ajouté au panier : @TITLE@';
          announce(addedTpl.replace('@TITLE@', addedItem.product_title || addedItem.title || ''));
        });
      })
      .catch(function (err) {
        // Progressive enhancement fallback: let the form submit normally
        hideLoading();
        form.removeAttribute('data-ajax-pending');
        form.submit();
      });
  }

  /* ---- Intercept product form submits ---- */
  function bindProductForms() {
    // Target forms with data-product-form attribute or action="/cart/add"
    document.querySelectorAll('form[data-product-form], form[action="/cart/add"]').forEach(function (form) {
      if (form.dataset.cartBound) return; // avoid double-bind
      form.dataset.cartBound = '1';

      form.addEventListener('submit', function (e) {
        // Check if variant is selected (id hidden input must have a value)
        var idInput = form.querySelector('input[name="id"]');
        if (!idInput || !idInput.value) return; // let form post normally

        e.preventDefault();

        if (form.dataset.ajaxPending) return; // debounce
        form.dataset.ajaxPending = '1';

        addToCart(form);

        // Always clean pending flag after a short window
        setTimeout(function () {
          delete form.dataset.ajaxPending;
        }, 3000);
      });
    });
  }

  // Bind on DOMContentLoaded (script is defer — DOM already ready)
  bindProductForms();

  // Re-bind if new forms are injected dynamically (quick-add, etc.)
  var _mutationObserver = window.MutationObserver
    ? new MutationObserver(function () { bindProductForms(); })
    : null;
  if (_mutationObserver) {
    _mutationObserver.observe(document.body, { childList: true, subtree: true });
    // Disconnect on navigation/unload to prevent memory leaks in bfcache-capable browsers
    window.addEventListener('pagehide', function () { _mutationObserver.disconnect(); });
  }

  /* ---- Expose API on window for external use ---- */
  window.V4CartDrawer = {
    open: openDrawer,
    close: closeDrawer,
    refresh: function () {
      showLoading();
      fetch('/cart.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' }
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          renderCart(cart);
          updateHeaderCount(cart.item_count);
          hideLoading();
        })
        .catch(hideLoading);
    }
  };
})();
